/**
 * DisclosureChecklist Component
 * Single Responsibility: Track and validate required disclosures
 */

import React, { useState, useEffect } from 'react'
import { CheckCircle, Circle, AlertCircle, Info } from 'lucide-react'
import { DisclosureItem } from '../../../types/meeting'

interface DisclosureChecklistProps {
  disclosures: DisclosureItem[]
  onDisclosureToggle: (disclosureId: string, completed: boolean) => void
  readonly?: boolean
}

const DisclosureChecklist: React.FC<DisclosureChecklistProps> = ({
  disclosures,
  onDisclosureToggle,
  readonly = false
}) => {
  const [localDisclosures, setLocalDisclosures] = useState<DisclosureItem[]>(disclosures)

  useEffect(() => {
    setLocalDisclosures(disclosures)
  }, [disclosures])

  const handleToggle = (disclosureId: string) => {
    if (readonly) return

    const updatedDisclosures = localDisclosures.map(disclosure =>
      disclosure.id === disclosureId
        ? { ...disclosure, completed: !disclosure.completed }
        : disclosure
    )
    setLocalDisclosures(updatedDisclosures)

    const toggledDisclosure = updatedDisclosures.find(d => d.id === disclosureId)
    if (toggledDisclosure) {
      onDisclosureToggle(disclosureId, toggledDisclosure.completed)
    }
  }

  const requiredDisclosures = localDisclosures.filter(d => d.required)
  const optionalDisclosures = localDisclosures.filter(d => !d.required)
  const completedCount = localDisclosures.filter(d => d.completed).length
  const requiredCompletedCount = requiredDisclosures.filter(d => d.completed).length
  const allRequiredComplete = requiredDisclosures.every(d => d.completed)

  if (disclosures.length === 0) {
    return (
      <div className="text-center py-4 text-slate-500">
        No disclosures required for this document
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Progress Summary */}
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-slate-800">Disclosure Checklist</h3>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            allRequiredComplete
              ? 'bg-green-100 text-green-700'
              : 'bg-yellow-100 text-yellow-700'
          }`}>
            {completedCount}/{localDisclosures.length} Complete
          </span>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-slate-200 rounded-full h-2 mb-3">
          <div
            className={`h-2 rounded-full transition-all ${
              allRequiredComplete ? 'bg-green-500' : 'bg-yellow-500'
            }`}
            style={{ width: `${(completedCount / localDisclosures.length) * 100}%` }}
          />
        </div>

        {/* Status Message */}
        {allRequiredComplete ? (
          <div className="flex items-center gap-2 text-sm text-green-600">
            <CheckCircle className="w-4 h-4" />
            All required disclosures have been made
          </div>
        ) : (
          <div className="flex items-center gap-2 text-sm text-yellow-600">
            <AlertCircle className="w-4 h-4" />
            {requiredDisclosures.length - requiredCompletedCount} required disclosure{requiredDisclosures.length - requiredCompletedCount !== 1 ? 's' : ''} remaining
          </div>
        )}
      </div>

      {/* Required Disclosures */}
      {requiredDisclosures.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-slate-700 flex items-center gap-2">
            <span className="text-red-500">*</span>
            Required Disclosures
          </h4>
          <div className="space-y-2">
            {requiredDisclosures.map(disclosure => (
              <DisclosureCheckItem
                key={disclosure.id}
                disclosure={disclosure}
                onToggle={() => handleToggle(disclosure.id)}
                readonly={readonly}
              />
            ))}
          </div>
        </div>
      )}

      {/* Optional Disclosures */}
      {optionalDisclosures.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-slate-700">
            Optional Disclosures
          </h4>
          <div className="space-y-2">
            {optionalDisclosures.map(disclosure => (
              <DisclosureCheckItem
                key={disclosure.id}
                disclosure={disclosure}
                onToggle={() => handleToggle(disclosure.id)}
                readonly={readonly}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// Individual disclosure item component
const DisclosureCheckItem: React.FC<{
  disclosure: DisclosureItem
  onToggle: () => void
  readonly?: boolean
}> = ({ disclosure, onToggle, readonly }) => {
  return (
    <div
      className={`flex items-start gap-3 p-3 rounded-lg border transition-all ${
        disclosure.completed
          ? 'bg-green-50 border-green-200'
          : disclosure.required
          ? 'bg-red-50 border-red-200'
          : 'bg-white border-slate-200'
      } ${!readonly ? 'hover:shadow-sm cursor-pointer' : ''}`}
      onClick={!readonly ? onToggle : undefined}
    >
      <button
        type="button"
        className={`mt-0.5 ${readonly ? 'cursor-default' : 'cursor-pointer'}`}
        onClick={(e) => {
          if (!readonly) {
            e.stopPropagation()
            onToggle()
          }
        }}
        disabled={readonly}
      >
        {disclosure.completed ? (
          <CheckCircle className="w-5 h-5 text-green-500" />
        ) : (
          <Circle className="w-5 h-5 text-slate-400" />
        )}
      </button>

      <div className="flex-1">
        <div className="flex items-start justify-between">
          <label className={`text-sm font-medium ${
            disclosure.completed ? 'text-green-700' : 'text-slate-800'
          } ${!readonly ? 'cursor-pointer' : ''}`}>
            {disclosure.label}
            {disclosure.required && (
              <span className="text-red-500 ml-1">*</span>
            )}
          </label>
        </div>

        {disclosure.description && (
          <p className="text-xs text-slate-600 mt-1">
            {disclosure.description}
          </p>
        )}

        {disclosure.completed && (
          <div className="flex items-center gap-1 mt-2 text-xs text-green-600">
            <CheckCircle className="w-3 h-3" />
            <span>Disclosed</span>
          </div>
        )}
      </div>

      {disclosure.description && (
        <div className="group relative">
          <Info className="w-4 h-4 text-slate-400" />
          <div className="absolute right-0 top-6 w-64 p-2 bg-slate-800 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
            {disclosure.description}
          </div>
        </div>
      )}
    </div>
  )
}

export default DisclosureChecklist